import React from 'react';
import { Task } from '../../types/task.types';
import { TaskGroup } from '../../hooks/useTaskQueries';
import { CheckboxState, checkboxState, dueStamp, isTaskOverdue, TASK_STATUS_LABEL } from './taskMeta';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

const CHECKBOX_STYLE: Record<CheckboxState, string> = {
  empty: 'border border-ink',
  half: 'border border-ink bg-[linear-gradient(90deg,var(--color-ink)_50%,transparent_50%)]',
  done: 'border border-ink bg-ink',
  void: 'border border-rule-faint',
};

export interface TaskGroupsProps {
  groups: TaskGroup[];
  selectedId: string | null;
  busy: boolean;
  onSelect: (id: string) => void;
  onToggleComplete: (task: Task) => void;
  onClaim: (task: Task) => void;
  onOpenRisk: (riskId: string) => void;
  onResetView: () => void;
}

const TaskRow: React.FC<{
  task: Task;
  props: TaskGroupsProps;
}> = ({ task, props }) => {
  const done = task.status === 'COMPLETED';
  const overdue = isTaskOverdue(task);
  const selected = task.id === props.selectedId;
  return (
    <div
      data-testid="task-row"
      onClick={() => props.onSelect(task.id)}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex cursor-pointer items-baseline gap-2.5 border-b border-rule-faint py-1.5 text-scale-1 hover:bg-accent-soft',
        selected && 'bg-accent-soft shadow-[inset_2px_0_0_var(--color-accent)]',
      )}
    >
      <button
        type="button"
        aria-label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
        disabled={props.busy || task.status === 'CANCELLED'}
        onClick={(event) => {
          event.stopPropagation();
          props.onToggleComplete(task);
        }}
        className={cn(
          'h-2.5 w-2.5 shrink-0 cursor-pointer self-center disabled:cursor-default',
          CHECKBOX_STYLE[checkboxState(task)],
        )}
      />
      <span className="hidden font-mono text-scale-0 text-ink-muted md:inline">
        {task.reference_number}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          {/* Named target: the row is clickable but carries nested buttons (the
              risk chip below), so a click aimed at the row box can land on one
              of them instead of selecting the task. */}
          <span
            data-testid="task-row-title"
            className={cn('truncate', done && 'text-ink-muted line-through')}
          >
            {task.title}
          </span>
          {task.riskReference && task.risk && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                props.onOpenRisk(task.risk as string);
              }}
              className="shrink-0 cursor-pointer font-mono text-scale-0 text-accent hover:underline"
            >
              ↳ {task.riskReference}
            </button>
          )}
        </span>
        <span className="mt-0.5 flex justify-between font-mono text-scale-0 text-ink-muted md:hidden">
          <span className="truncate">
            {task.reference_number} · {TASK_STATUS_LABEL[task.status]}
            {task.assigneeName ? ` · ${task.assigneeName}` : ''}
          </span>
          <span className={cn(overdue && 'text-sev-critical')}>{dueStamp(task) ?? '—'}</span>
        </span>
      </span>
      {task.assignee ? (
        <span className="hidden w-32 truncate text-scale-0 text-ink-muted md:inline">
          {task.assigneeName || task.assigneeEmail || '—'}
        </span>
      ) : (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            props.onClaim(task);
          }}
          className="hidden w-32 cursor-pointer text-left text-scale-0 text-ink-muted md:inline"
        >
          unassigned <span className="font-serif italic hover:text-accent">— claim?</span>
        </button>
      )}
      <span
        className={cn(
          'hidden w-14 text-right font-mono text-scale-0 md:inline',
          overdue ? 'text-sev-critical' : 'text-ink-muted',
        )}
      >
        {dueStamp(task) ?? '—'}
      </span>
    </div>
  );
};

export const TaskGroups: React.FC<TaskGroupsProps> = (props) => {
  const { groups } = props;
  if (groups.every((group) => group.isPending)) return <LedgerSkeleton lines={8} className="mt-4" />;

  if (groups.every((group) => !group.isPending && group.count === 0)) {
    return (
      <p className="py-6 font-serif text-scale-1 italic text-ink-muted">
        Nothing on file for this view.{' '}
        <button type="button" onClick={props.onResetView} className="cursor-pointer underline hover:text-accent">
          Show all tasks
        </button>
      </p>
    );
  }

  return (
    <div>
      {groups.map((group) => {
        if (!group.isPending && group.count === 0) return null;
        return (
          <section key={group.key} aria-label={group.label}>
            <div
              className={cn(
                'cap-label mt-4 border-b border-ink pb-0.5',
                group.key === 'overdue' && '!text-sev-critical',
              )}
            >
              {group.label} · {group.count}
            </div>
            {group.isPending ? (
              <LedgerSkeleton lines={2} className="mt-1" />
            ) : (
              group.rows.map((task) => (
                <TaskRow key={task.id} task={task} props={props} />
              ))
            )}
            {group.hasMore && (
              <button
                type="button"
                onClick={group.fetchMore}
                disabled={group.isFetchingMore}
                className="cursor-pointer py-1.5 font-serif text-scale-1 italic text-ink-muted underline hover:text-accent disabled:opacity-40"
              >
                Show {Math.max(0, group.count - group.rows.length)} more
              </button>
            )}
          </section>
        );
      })}
      <p className="mt-2 font-serif text-scale-1 italic text-ink-muted">j/k to move, Enter to peek</p>
    </div>
  );
};
